import { useCallback } from 'react';
import { unwrapResult } from '@reduxjs/toolkit';
import {
  ProductCreateRequestDto,
  ProductDetailsResponseDto,
} from '@tiendanube/common';
import { CATALOG_UNSTABLE_API_V2 } from 'App/featuresFlags';
import { useHasTags } from 'App/hooks';
import { LanguagesType } from 'App/i18n';
import { AppDispatchType, useAppDispatch } from 'App/store';
import { useGetLanguage } from 'domains/Auth/hooks';
import { cleanUpInventory } from 'domains/Catalog/Inventory/inventorySlice';
import { generateProductPayload } from './generateProductPayload/generateProductPayload';
import { createResponseDto } from './generateProductPayload/helpers';
import {
  addProduct as addProductAction,
  addProductWithUnstableApi as addProductWithUnstableApiAction,
} from '../../../productsSlice';

type addProductType = (
  product: ProductCreateRequestDto,
) => Promise<ProductDetailsResponseDto>;

interface UseCreateProductResult {
  addProduct: addProductType;
}

function useCreateProduct(): UseCreateProductResult {
  const dispatch = useAppDispatch();
  const language = useGetLanguage();
  const [isApiV2] = useHasTags([CATALOG_UNSTABLE_API_V2]);

  const addProduct: addProductType = useCallback(
    async (product) => {
      if (isApiV2) {
        return applyApiV2(dispatch, product, language);
      } else {
        return applyApiV1(dispatch, product);
      }
    },
    [dispatch, isApiV2, language],
  );

  return { addProduct };
}

async function applyApiV2(
  dispatch: AppDispatchType,
  product: ProductCreateRequestDto,
  language: LanguagesType,
): Promise<ProductDetailsResponseDto> {
  const result = await dispatch(
    addProductWithUnstableApiAction({
      product: generateProductPayload(product, language),
    }),
  );
  const productDetailsResponse = unwrapResult(result);
  const createdId = productDetailsResponse.id;
  dispatch(cleanUpInventory());
  return createResponseDto(createdId);
}

async function applyApiV1(
  dispatch: AppDispatchType,
  product: ProductCreateRequestDto,
): Promise<ProductDetailsResponseDto> {
  const result = await dispatch(addProductAction({ product }));
  const productResponse = unwrapResult(result);
  dispatch(cleanUpInventory());
  return productResponse;
}

export default useCreateProduct;
